import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["value", "onBlur", "onFocus", "onChange"],
  _excluded2 = ["label", "fullWidth", "helperText", "suspenseFallback"];
import { TextField } from '@material-ui/core';
import { mergeRefs } from '@superdispatch/ui';
import { forwardRef, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePhoneService } from "../phone-service/PhoneService.js";
import { PhoneFieldMenu } from "./PhoneFieldMenu.js";
import { PhoneFieldStartAdornment } from "./PhoneFieldStartAdornment.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
function normalizeValue(value) {
  return typeof value === 'string' ? value : '';
}
export var PhoneField = /*#__PURE__*/forwardRef((_ref, ref) => {
  var {
      value: valueProp,
      onBlur: _onBlur,
      onFocus: _onFocus,
      onChange: _onChange
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  var rootRef = useRef(null);
  var inputRef = useRef(null);
  var [menuAnchor, setMenuAnchor] = useState(null);
  var phoneService = usePhoneService();
  var createState = useCallback(value => _objectSpread({
    value
  }, phoneService.getInfo(value)), [phoneService]);
  var value = useMemo(() => normalizeValue(valueProp), [valueProp]);
  var [{
    country,
    nationalNumber
  }, setValue] = useState(() => createState(value));
  var placeholder = useMemo(() => {
    var _phoneService$APN$get;
    return ((_phoneService$APN$get = phoneService.APN.getExample(country).number) === null || _phoneService$APN$get === void 0 ? void 0 : _phoneService$APN$get.international) || '';
  }, [country, phoneService.APN]);
  function handleChange(fn, nextCountry, nextNationalNumber) {
    if (fn) {
      var nextValue = phoneService.format(nextNationalNumber, {
        country: nextCountry
      });
      setValue({
        value: nextValue,
        country: nextCountry,
        nationalNumber: nextNationalNumber
      });
      fn(nextValue);
    }
  }
  function handleChangeEvent(fn, _ref2) {
    var {
      target: {
        value: nextValue
      }
    } = _ref2;
    if (fn) {
      handleChange(fn, country, phoneService.format(nextValue, {
        country,
        format: 'national'
      }));
    }
  }
  useEffect(() => {
    setValue(prev =>
    // Ignore `props.value` changes when they were performed from inside.
    prev.value === value ? prev : createState(value));
  }, [value, createState]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PhoneFieldMenu, {
      value: country,
      anchorEl: menuAnchor,
      onClose: () => {
        setMenuAnchor(null);
      },
      onChange: nextRegion => {
        if (!props.disabled) {
          handleChange(_onChange, nextRegion, nationalNumber);
        }
      }
    }), /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({}, props), {}, {
      type: "tel",
      variant: "outlined",
      autoComplete: "off",
      value: nationalNumber,
      placeholder: phoneService.deletePrefix(placeholder, country),
      ref: mergeRefs(ref, rootRef),
      inputRef: inputRef,
      onBlur: event => {
        handleChangeEvent(_onBlur, event);
      },
      onFocus: event => {
        handleChangeEvent(_onFocus, event);
      },
      onChange: event => {
        handleChangeEvent(_onChange, event);
      },
      InputProps: {
        startAdornment: /*#__PURE__*/_jsx(PhoneFieldStartAdornment, {
          country: country,
          disabled: props.disabled,
          isExpanded: !!menuAnchor,
          onClick: () => {
            var _inputRef$current;
            // `FocusTrap` inside of `Menu` will restore focus to
            // the last focused element. We want to manually focus input
            // to trick the `FocusTrap`.
            (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
            setMenuAnchor(rootRef.current);
          }
        })
      }
    }))]
  });
});
if (process.env.NODE_ENV !== "production") PhoneField.displayName = "PhoneField";
export var SuspendedPhoneField = /*#__PURE__*/forwardRef((_ref3, ref) => {
  var {
      label,
      fullWidth,
      helperText,
      suspenseFallback = /*#__PURE__*/_jsx(TextField, {
        disabled: true,
        label: label,
        fullWidth: fullWidth,
        helperText: helperText,
        placeholder: "Loading\u2026"
      })
    } = _ref3,
    props = _objectWithoutProperties(_ref3, _excluded2);
  return /*#__PURE__*/_jsx(Suspense, {
    fallback: suspenseFallback,
    children: /*#__PURE__*/_jsx(PhoneField, _objectSpread(_objectSpread({}, props), {}, {
      ref: ref,
      label: label,
      fullWidth: fullWidth,
      helperText: helperText
    }))
  });
});
if (process.env.NODE_ENV !== "production") SuspendedPhoneField.displayName = "SuspendedPhoneField";